import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  AcademicCapIcon,
  CubeIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import logoImage from '../assets/Pingfence Logo.png';
import { products } from '../data/products';

const categories = [...new Set(products.map(product => product.category))];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isMobileCategoriesOpen, setIsMobileCategoriesOpen] = useState(false);
  const categoriesRef = useRef(null);

  const navigationItems = [
    {
      name: "Products",
      icon: CubeIcon,
      path: "/products"
    },
    {
      name: "My Purchases",
      icon: ArrowDownTrayIcon,
      path: "/my-purchases"
    }
  ];

  // Handle click outside the categories dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoriesRef.current && !categoriesRef.current.contains(event.target)) {
        setIsCategoriesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
    <nav className="bg-secondary py-2.5 sm:py-4 border-b-2 sm:border-b-4 border-primary sticky top-0 z-50">
      <div className="container">
        {/* Main Navigation Bar */}
        <div className="flex items-center justify-between gap-3 sm:gap-4 lg:gap-8">
          <Link to="/" className="flex-shrink-0 flex items-center gap-2 sm:gap-3 text-2xl sm:text-3xl font-black text-black uppercase tracking-tighter" style={{textShadow: '2px 2px 0px #9FE240'}}>
            <img 
              src={logoImage} 
              alt="DigitalAssets Logo" 
              className="h-8 sm:h-10 w-auto object-contain hover:scale-105 transition-all duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4 lg:space-x-6 flex-shrink-0">
            {/* Categories Dropdown */}
            <div className="relative" ref={categoriesRef}>
              <button
                type="button"
                onClick={() => setIsCategoriesOpen(!isCategoriesOpen)}
                className="flex items-center space-x-1 text-black font-bold hover:text-primary transition-colors py-2 uppercase tracking-wide cursor-pointer"
              >
                <Squares2X2Icon className="h-6 w-6 font-bold" />
                <span className="hidden xl:inline">Categories</span>
                <ChevronDownIcon className={`h-4 w-4 font-bold transition-transform duration-200 ${isCategoriesOpen ? 'rotate-180' : ''}`} />
              </button>

              {isCategoriesOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white border-4 border-black shadow-[6px_6px_0px_0px_#000000] z-50 py-2 max-h-[70vh] overflow-y-auto rounded-none">
                  <div className="px-4 py-2 border-b-2 border-black flex items-center justify-between bg-primary/20">
                    <span className="text-xs font-black uppercase tracking-wider text-black">Asset Categories</span>
                    <span className="text-[10px] font-black uppercase bg-black text-primary px-1.5 py-0.5 border border-black">
                      {categories.length} Topics
                    </span>
                  </div>

                  <Link
                    to="/products"
                    onClick={() => setIsCategoriesOpen(false)}
                    className="flex items-center justify-between px-4 py-2.5 text-sm font-black uppercase text-black hover:bg-primary border-b-2 border-black transition-colors"
                  >
                    <span>All Products</span>
                    <span className="text-xs font-black">→</span>
                  </Link>

                  <div className="divide-y divide-gray-100">
                    {categories.map((category) => (
                      <Link
                        key={category}
                        to={`/products?category=${encodeURIComponent(category)}`}
                        onClick={() => setIsCategoriesOpen(false)}
                        className="block px-4 py-2 text-sm font-bold text-black hover:bg-primary hover:text-black transition-colors"
                      >
                        {category}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Digital Bundles Link */}
            <Link 
              to="/digital-bundles" 
              className="flex items-center space-x-1 text-black font-bold hover:text-primary transition-colors py-2"
            >
              <AcademicCapIcon className="h-6 w-6 font-bold" />
              <span className="hidden xl:inline uppercase tracking-wide">Digital Bundles</span>
            </Link>

            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className="flex items-center space-x-1 text-black font-bold hover:text-primary transition-colors py-2"
              >
                <item.icon className="h-6 w-6 font-bold" />
                <span className="hidden xl:inline uppercase tracking-wide">{item.name}</span>
              </Link>
            ))}
          </div>

          {/* Primary marketplace action */}
          <Link
            to="/products"
            className="hidden md:flex items-center gap-2 ml-auto bg-primary border-2 border-black px-3 py-2 text-xs lg:text-sm font-black uppercase text-black shadow-[3px_3px_0px_0px_#000000] transition-all hover:-translate-y-0.5 hover:bg-primary-light hover:shadow-[4px_4px_0px_0px_#000000]"
          >
            <CubeIcon className="h-5 w-5" />
            <span className="hidden lg:inline">Explore Free Assets</span>
          </Link>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-black hover:text-primary"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden mt-3 space-y-1.5 max-h-[80vh] overflow-y-auto pb-3">
              {/* Mobile Categories Accordion */}
              <div>
                <button
                  type="button"
                  onClick={() => setIsMobileCategoriesOpen(!isMobileCategoriesOpen)}
                  className="w-full flex items-center justify-between text-black px-3 py-1.5 hover:text-primary transition-colors font-bold uppercase tracking-wide text-left text-xs sm:text-sm"
                >
                  <div className="flex items-center space-x-2">
                    <Squares2X2Icon className="h-4 w-4" />
                    <span>Categories</span>
                  </div>
                  <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${isMobileCategoriesOpen ? 'rotate-180' : ''}`} />
                </button>
                {isMobileCategoriesOpen && (
                  <div className="pl-4 pr-2 py-1 space-y-1 max-h-52 overflow-y-auto bg-gray-50 border-l-2 border-black ml-3 my-1">
                    <Link
                      to="/products"
                      onClick={() => { setIsMenuOpen(false); setIsMobileCategoriesOpen(false); }}
                      className="block py-1 text-xs font-black uppercase text-black hover:text-primary"
                    >
                      All Products
                    </Link>
                    {categories.map((category) => (
                      <Link
                        key={category}
                        to={`/products?category=${encodeURIComponent(category)}`}
                        onClick={() => { setIsMenuOpen(false); setIsMobileCategoriesOpen(false); }}
                        className="block py-1 text-xs font-bold text-gray-800 hover:text-primary"
                      >
                        {category}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Digital Bundles Link */}
              <Link
                to="/digital-bundles"
                className="flex items-center space-x-2 text-black px-3 py-1.5 hover:text-primary transition-colors text-xs sm:text-sm font-bold uppercase"
                onClick={() => setIsMenuOpen(false)}
              >
                <AcademicCapIcon className="h-4 w-4" />
                <span>Digital Bundles</span>
              </Link>

              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="flex items-center space-x-2 text-black px-3 py-1.5 hover:text-primary transition-colors text-xs sm:text-sm font-bold uppercase"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.name}</span>
                </Link>
              ))}

              <Link
                to="/products"
                className="mt-2 flex items-center justify-center gap-2 border-2 border-black bg-primary px-3 py-2 text-xs font-black uppercase text-black shadow-[3px_3px_0px_0px_#000000]"
                onClick={() => setIsMenuOpen(false)}
              >
                <CubeIcon className="h-4 w-4" />
                <span>Explore Free Assets</span>
              </Link>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};

export default Navbar; 